import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Download, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Checkbox, Input, Select, Textarea } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import { categoriesQuery, categoryLabel } from '../../lib/queries';
import { useCan } from '../../lib/session';
import type { ImportRunSummary, ImportSource, SupplierOffer } from '../../lib/types';
import { formatDateTime, formatMoney } from '../../lib/utils';
import { RunStatusBadge } from '../imports/run';
import { suppliersQuery } from './list';
import { SourceDialog } from './source-dialog';

export function SupplierDetailPage({ supplierId }: { supplierId: string }) {
  const { t, i18n } = useTranslation();
  const canWrite = useCan('suppliers.write');
  const suppliers = useQuery(suppliersQuery);
  const sources = useQuery({
    queryKey: ['suppliers', supplierId, 'sources'],
    queryFn: () => api<{ items: ImportSource[] }>(`/suppliers/${supplierId}/sources`),
  });
  const offers = useInfiniteQuery({
    queryKey: ['suppliers', supplierId, 'offers'],
    queryFn: ({ pageParam }) =>
      api<{ items: SupplierOffer[]; nextAfter: string | null }>(`/suppliers/${supplierId}/offers`, {
        query: { limit: 100, after: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextAfter ?? undefined,
  });
  const [editing, setEditing] = useState<ImportSource | 'new'>();
  const [creatingProducts, setCreatingProducts] = useState(false);
  const canRunImports = useCan('imports.run');
  const canWriteCatalog = useCan('catalog.write');
  const canCreateProducts = canRunImports && canWriteCatalog;
  const queryClient = useQueryClient();
  const [settingsError, setSettingsError] = useState<string>();
  const setManualOrders = async (manualOrders: boolean) => {
    setSettingsError(undefined);
    try {
      await api(`/suppliers/${supplierId}`, { method: 'PATCH', body: { manualOrders } });
      await queryClient.invalidateQueries({ queryKey: suppliersQuery.queryKey });
    } catch (err) {
      setSettingsError(errorMessage(err));
    }
  };
  const row = suppliers.data?.items.find((r) => r.supplier.id === supplierId);

  const offerColumns = useMemo<Columns<SupplierOffer>>(
    () => [
      { id: 'sku', header: t('products.sku'), accessorFn: (o) => o.supplierSku },
      { id: 'title', header: t('common.name'), accessorFn: (o) => o.title ?? '' },
      { id: 'gtin', header: t('products.gtin'), accessorFn: (o) => o.gtin ?? '' },
      {
        id: 'cost',
        header: t('suppliers.cost'),
        accessorFn: (o) => o.costMinor ?? 0,
        cell: ({ row: r }) => formatMoney(r.original.costMinor, r.original.currency, i18n.language),
      },
      { id: 'stock', header: t('suppliers.stock'), accessorFn: (o) => o.stockQty },
      {
        id: 'matched',
        header: t('suppliers.matched'),
        accessorFn: (o) => !!o.variantId,
        cell: ({ row: r }) =>
          r.original.variantId ? (
            <Badge variant="success">{t('common.yes')}</Badge>
          ) : (
            <Badge variant="warning">{t('common.no')}</Badge>
          ),
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <Link
        to="/suppliers"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('nav.suppliers')}
      </Link>
      <PageHeader
        title={row?.supplier.name ?? '…'}
        description={
          row
            ? `${row.supplier.code} · ${row.supplier.countryCode} · ${t('suppliers.offers')}: ${String(row.offers)} · ${t('suppliers.unmatched')}: ${String(row.unmatched)}`
            : undefined
        }
      />
      {row ? (
        <label className="mb-4 flex items-start gap-2 text-sm">
          <Checkbox
            checked={row.supplier.manualOrders}
            disabled={!canWrite}
            onChange={(e) => void setManualOrders(e.target.checked)}
          />
          <span>
            {t('suppliers.manualOrders')}
            <span className="block text-muted-foreground">{t('suppliers.manualOrdersHint')}</span>
          </span>
        </label>
      ) : null}
      {settingsError ? <Alert variant="destructive">{settingsError}</Alert> : null}
      <div className="grid gap-6">
        <Card>
          <CardHeader
            title={t('suppliers.sources')}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing('new');
                  }}
                >
                  <Plus />
                  {t('suppliers.newSource')}
                </Button>
              ) : null
            }
          />
          <CardContent className="grid gap-4">
            {sources.error ? (
              <Alert variant="destructive">{errorMessage(sources.error)}</Alert>
            ) : null}
            {(sources.data?.items ?? []).length === 0 && !sources.isPending ? (
              <p className="text-sm text-muted-foreground">{t('common.empty')}</p>
            ) : null}
            {(sources.data?.items ?? []).map((source) => (
              <SourceCard
                key={source.id}
                source={source}
                onEdit={
                  canWrite
                    ? () => {
                        setEditing(source);
                      }
                    : undefined
                }
              />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={t('suppliers.offersTitle')}
            actions={
              canCreateProducts ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCreatingProducts(true);
                  }}
                >
                  <Plus />
                  {t('suppliers.createProducts')}
                </Button>
              ) : null
            }
          />
          <CardContent className="grid gap-4">
            <DataTable
              columns={offerColumns}
              data={offers.data?.pages.flatMap((p) => p.items) ?? []}
              loading={offers.isPending}
              rowKey={(o) => o.id}
            />
            {offers.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  disabled={offers.isFetchingNextPage}
                  onClick={() => void offers.fetchNextPage()}
                >
                  {t('common.loadMore')}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {creatingProducts ? (
        <CreateProductsDialog
          supplierId={supplierId}
          onClose={() => {
            setCreatingProducts(false);
          }}
        />
      ) : null}
      {editing ? (
        <SourceDialog
          supplierId={supplierId}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Товары из предложений поставщика: список его артикулов (по одному в строке) и категория.
 * Создаются черновики с фото — в фоне; уже связанные предложения пропускаются.
 */
function CreateProductsDialog({
  supplierId,
  onClose,
}: {
  supplierId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const categories = useQuery(categoriesQuery);
  const [text, setText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const skus = [
    ...new Set(
      text
        .split(/[\n,;\t]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
  const submit = async () => {
    setError(undefined);
    try {
      const result = await api<{ count: number }>(`/suppliers/${supplierId}/create-products`, {
        method: 'POST',
        body: { supplierSkus: skus, ...(categoryId ? { categoryId } : {}) },
      });
      setNotice(t('suppliers.createProductsQueued', { count: result.count }));
      setText('');
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const all = categories.data ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('suppliers.createProducts')}
      className="max-w-xl"
      footer={
        <Button disabled={skus.length === 0 || skus.length > 1000} onClick={() => void submit()}>
          {t('suppliers.createProductsSubmit', { count: skus.length })}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        <p className="text-muted-foreground">{t('suppliers.createProductsHint')}</p>
        <Field label={t('pricing.scopes.category')}>
          {(id) => (
            <Select
              id={id}
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
              }}
            >
              <option value="">—</option>
              {all.map((c) => (
                <option key={c.id} value={c.id}>
                  {categoryLabel(c, all, 'ru')}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('suppliers.createProductsSkus')}>
          {(id) => (
            <Textarea
              id={id}
              rows={10}
              className="font-mono"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
          )}
        </Field>
        {notice ? <Alert>{notice}</Alert> : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

function SourceCard({ source, onEdit }: { source: ImportSource; onEdit?: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canRun = useCan('imports.run');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const runs = useQuery({
    queryKey: ['suppliers', 'source-runs', source.id],
    queryFn: () => api<{ items: ImportRunSummary[] }>(`/suppliers/sources/${source.id}/runs`),
    enabled: canRun,
    // Пока идёт обработка — обновляем журнал.
    refetchInterval: (query) =>
      query.state.data?.items.some((r) => r.status === 'running' && !r.stats.phase) ? 3000 : false,
  });

  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File) =>
    void act(async () => {
      const form = new FormData();
      form.append('file', file);
      const result = await api<{ syncRunId: string | null; duplicate: boolean }>(
        `/suppliers/sources/${source.id}/upload`,
        { method: 'POST', form },
      );
      if (result.duplicate || !result.syncRunId) {
        setNotice(t('suppliers.duplicate'));
        return;
      }
      await navigate({ to: '/imports/$runId', params: { runId: result.syncRunId } });
    });

  const runColumns: Columns<ImportRunSummary> = [
    {
      id: 'started',
      header: t('imports.started'),
      accessorFn: (r) => r.startedAt,
      cell: ({ row }) => formatDateTime(row.original.startedAt, i18n.language),
    },
    { id: 'job', header: t('imports.job'), accessorFn: (r) => r.job },
    {
      id: 'status',
      header: t('common.status'),
      accessorFn: (r) => r.status,
      cell: ({ row }) => <RunStatusBadge run={row.original} />,
    },
    {
      id: 'rows',
      header: t('imports.rows'),
      accessorFn: (r) => r.stats.rows?.total ?? 0,
    },
  ];

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">
            {source.name}{' '}
            <Badge variant={source.status === 'active' ? 'success' : 'warning'}>
              {t(`suppliers.sourceStatus.${source.status as 'active' | 'degraded' | 'disabled'}`)}
            </Badge>
          </p>
          <p className="text-xs text-muted-foreground">
            {source.mapping.format.toUpperCase()} · {t(`suppliers.feed.${source.config.feedMode}`)}{' '}
            · {t(`suppliers.transportType.${source.config.transport.type}`)} ·{' '}
            {t('suppliers.lastSuccess')}: {formatDateTime(source.lastSuccessAt, i18n.language)}
          </p>
          {source.lastError ? (
            <p className="mt-1 text-xs text-destructive">
              {t('suppliers.lastError')}: {source.lastError}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {onEdit ? (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil />
              {t('common.edit')}
            </Button>
          ) : null}
          {canRun && source.config.transport.type === 'url' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api(`/suppliers/sources/${source.id}/fetch`, { method: 'POST' });
                  setNotice(t('suppliers.fetchQueued'));
                  await runs.refetch();
                })
              }
            >
              <Download />
              {t('suppliers.fetchNow')}
            </Button>
          ) : null}
        </div>
      </div>
      {canRun ? (
        <div className="mb-3 max-w-md">
          <Input
            type="file"
            aria-label={t('suppliers.upload')}
            disabled={busy}
            accept=".csv,.txt,.xml,.json,.jsonl,.xlsx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload(file);
              e.target.value = '';
            }}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('suppliers.upload')}</p>
        </div>
      ) : null}
      {notice ? <Alert className="mb-3">{notice}</Alert> : null}
      {error ? (
        <Alert variant="destructive" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {canRun ? (
        <>
          <p className="mb-2 text-sm font-medium">{t('suppliers.runs')}</p>
          <DataTable
            columns={runColumns}
            data={runs.data?.items ?? []}
            loading={runs.isPending}
            rowKey={(r) => r.id}
            onRowClick={(r) => {
              void navigate({ to: '/imports/$runId', params: { runId: r.id } });
            }}
          />
        </>
      ) : null}
    </div>
  );
}
